/**
 * Lo que toda pantalla de §P necesita saber, resuelto una vez.
 *
 * El club elegido, el rol en **ese** club y si está suspendido. Antes viajaban por
 * props desde `App.tsx`; con el enrutador cada pantalla es una ruta suya y
 * pasárselo por props significaría que cada ruta vuelva a calcularlo.
 *
 * ## Lo que NO va aquí
 *
 * Nada que se pueda pedir con una consulta. Esto es el contexto de la sesión en un
 * club, no una caché: los alumnos, los grupos y los recibos los trae TanStack Query
 * donde hacen falta.
 *
 * ## `pagadorDesdeTutor` vive en memoria a propósito
 *
 * Son **datos de una persona** —nombre y correo— y la URL acaba en un historial, en
 * una captura y en un chat. Se pierde al recargar, que es lo correcto: se vuelve a
 * la ficha y se pulsa otra vez.
 */
import { createContext, useContext, useMemo, useState } from 'react';
import {
  puedeCorregirAvisos,
  puedeEditarAlumnos,
  puedeEditarLaEscalera,
  puedeGenerarCalendario,
  puedeMoverUnaClase,
  puedePublicarAvisos,
  type Rol,
} from '@/club/permisos';
import type { DesdeTutor } from '@/club/pagadores/FormularioDePagador';
import {
  tieneFuncion,
  type FuncionDelPlan,
  type PlanDelClub,
} from '@/club/plan';

export interface ContextoDelClub {
  readonly club: string;
  readonly rol: Rol;
  /** Solo lectura: suspendido por nosotros, o con el pago vencido y sin gracia. */
  readonly suspendido: boolean;
  /** `undefined` mientras carga. Ver `club/plan.ts`. */
  readonly plan: PlanDelClub | undefined;
  /**
   * Si el **club** tiene una función de su plan. No dice nada de la persona: eso
   * siguen siendo los permisos de rol (§A.2.1, «el plan no es un rol»).
   */
  readonly tiene: (funcion: FuncionDelPlan) => boolean;
  /** Atajo del más usado: `puedeEditarAlumnos(rol)`, y nunca en solo lectura. */
  readonly puedeEscribir: boolean;
  /**
   * Las reglas de rol, agrupadas.
   *
   * Se pasan aquí en lugar de importarlas en cada ruta para que **las rutas no
   * decidan permisos**: su trabajo es traducir una URL en una pantalla, y cada
   * `import` de `permisos` en una ruta es un sitio más donde alguien puede escribir
   * la regla al revés.
   */
  readonly permisos: {
    readonly generarCalendario: (rol: Rol) => boolean;
    readonly moverUnaClase: (rol: Rol) => boolean;
    readonly editarLaEscalera: (rol: Rol) => boolean;
    readonly publicarAvisos: (rol: Rol) => boolean;
    readonly corregirAvisos: (rol: Rol) => boolean;
  };
  readonly pagadorDesdeTutor: DesdeTutor | undefined;
  readonly recordarTutor: (desde: DesdeTutor) => void;
  readonly olvidarTutor: () => void;
}

const Contexto = createContext<ContextoDelClub | null>(null);

export function ProveedorDelClub({
  club,
  rol,
  suspendido,
  plan,
  children,
}: {
  club: string;
  rol: Rol;
  suspendido: boolean;
  plan: PlanDelClub | undefined;
  children: React.ReactNode;
}) {
  const [pagadorDesdeTutor, setPagadorDesdeTutor] = useState<
    DesdeTutor | undefined
  >(undefined);

  const valor = useMemo<ContextoDelClub>(
    () => ({
      club,
      rol,
      suspendido,
      plan,
      tiene: (funcion) => tieneFuncion(plan, funcion),
      // En solo lectura no se escribe, sea cual sea el rol: sin esto, «Añadir
      // alumno» seguía a la vista con el club suspendido y respondía 423 al guardar.
      puedeEscribir: puedeEditarAlumnos(rol) && !suspendido,
      permisos: {
        generarCalendario: puedeGenerarCalendario,
        moverUnaClase: puedeMoverUnaClase,
        editarLaEscalera: puedeEditarLaEscalera,
        publicarAvisos: puedePublicarAvisos,
        corregirAvisos: puedeCorregirAvisos,
      },
      pagadorDesdeTutor,
      recordarTutor: setPagadorDesdeTutor,
      olvidarTutor: () => setPagadorDesdeTutor(undefined),
    }),
    [club, rol, suspendido, plan, pagadorDesdeTutor]
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/**
 * Falla ruidosamente fuera del marco.
 *
 * Una pantalla de §P sin club no es un caso a manejar: es una ruta mal colgada del
 * árbol. Devolver un club vacío la dejaría pidiendo `/clubs//students` y fallando
 * con un `404` que no menciona la causa.
 */
export function useContextoDelClub(): ContextoDelClub {
  const valor = useContext(Contexto);
  if (!valor) {
    throw new Error(
      'Esta pantalla está fuera del marco del panel: cuélgala de la ruta «panel».'
    );
  }
  return valor;
}
